import { ComponentProps, useState, useMemo, useEffect } from 'react';
import { Line } from 'react-chartjs-2';

import { GraphResponse } from '~/shared/types/api';

import { htmlLegendPlugin } from './htmlLegendPlugin';
import Legends, { ChartTypes } from './Legends';
import { ExternalTooltipHandler } from './ExternalTooltip';
import {
  calculateDateRange,
  checkIsSingleDayData,
  formatChartData,
  calculateYAxisScale,
  calculateXAxisConfig,
  createXAxisTickFormatter,
  createYAxisTickFormatter,
  filterFutureDataPoints,
} from './helpers';

export default function LineChartImpl({
  data,
  chartType = 'utilization',
}: {
  data: GraphResponse['utilizationGraph'];
  activeFilter: string;
  chartType?: ChartTypes;
}) {
  const filteredData = useMemo(() => filterFutureDataPoints(data), [data]);

  const hasDatafyDiskUsed = useMemo(
    () => !!filteredData?.some((d) => typeof d.datafyDiskUsed === 'number'),
    [filteredData]
  );

  const [visibleDatasets, setVisibleDatasets] = useState({
    utilized: chartType === 'utilization' || chartType === 'usage',
    size: chartType === 'usage',
    sourceSize: chartType === 'usage',
    datafyDiskUsed: hasDatafyDiskUsed,
  });

  useEffect(() => {
    setVisibleDatasets((prev) => ({
      ...prev,
      utilized: chartType === 'utilization' || chartType === 'usage',
      datafyDiskUsed: hasDatafyDiskUsed ? true : prev.datafyDiskUsed && hasDatafyDiskUsed,
      sourceSize: chartType === 'usage',
      size: chartType === 'usage',
    }));
  }, [chartType, hasDatafyDiskUsed]);

  const toggleDatasetVisibility = (datasetKey: keyof typeof visibleDatasets) => {
    setVisibleDatasets((prevState) => ({
      ...prevState,
      [datasetKey]: !prevState[datasetKey],
    }));
  };

  const [minDate, maxDate] = useMemo(() => calculateDateRange(filteredData), [filteredData]);

  const isSingleDayData = useMemo(() => checkIsSingleDayData(minDate, maxDate), [minDate, maxDate]);

  const chartData = useMemo(
    () => formatChartData(filteredData, visibleDatasets, chartType),
    [filteredData, visibleDatasets, chartType]
  );

  const allValidNumericValues = useMemo(() => {
    if (chartType === 'utilization') {
      return filteredData!
        .map((d) => {
          if (!d.size || d.size === 0) {
            return null;
          }
          return ((d.utilized || 0) / d.size) * 100;
        })
        .filter((value): value is number => typeof value === 'number' && !isNaN(value));
    } else {
      return filteredData!
        .flatMap((d) => {
          if (d.utilized === 0 && d.size === 0) {
            return [null, null, null, null];
          }
          return [
            visibleDatasets.utilized ? d.utilized : null,
            visibleDatasets.size ? d.size : null,
            visibleDatasets.sourceSize ? d.sourceSize : null,
            visibleDatasets.datafyDiskUsed ? d.datafyDiskUsed : null,
          ];
        })
        .filter((value): value is number => typeof value === 'number' && !isNaN(value));
    }
  }, [filteredData, chartType, visibleDatasets]);

  const yMin = 0;
  const actualMaxVal = chartType === 'utilization' ? 100 : Math.max(...allValidNumericValues, 0);

  const yMaxForScale = useMemo(() => {
    if (chartType === 'utilization') {
      return 100; // 0%, 25%, 50%, 75%, 100%
    }
    const { yMaxForScale: calculatedMax } = calculateYAxisScale(actualMaxVal, yMin);
    return calculatedMax;
  }, [actualMaxVal, chartType]);
  const options: ComponentProps<typeof Line>['options'] = useMemo(() => {
    const { xAxisUnit, xAxisStepSize, is18To36HourRange, isTwoDayShortRange } =
      calculateXAxisConfig(minDate, maxDate, isSingleDayData);

    const yAxisTickFormatter =
      chartType === 'utilization'
        ? (tickValue: unknown) => `${Number(tickValue)}%`
        : createYAxisTickFormatter(yMaxForScale, actualMaxVal);
    const xAxisTickFormatter = createXAxisTickFormatter(
      isSingleDayData,
      xAxisUnit,
      is18To36HourRange,
      // isSubHourRange,
      xAxisStepSize,
      minDate,
      maxDate,
      isTwoDayShortRange
    );

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
          external: ExternalTooltipHandler,
        },
      },
      elements: {
        line: {
          tension: 0,
          // borderCapStyle: 'round',
          // borderJoinStyle: 'round',
        },
        point: {
          radius: 0,
          hoverRadius: 5,
          hitRadius: 10,
          hoverBorderWidth: 2,
          pointStyle: 'circle',
        },
      },
      scales: {
        y: {
          border: {
            dash: [4, 4],
            width: 1,
            color: 'rgba(148, 158, 172, 0.5)',
          },
          max: yMaxForScale,
          min: yMin,
          ticks: {
            callback: yAxisTickFormatter,
            color: '#949EAC',
            font: { size: 16, family: 'Inter', lineHeight: 1.25, weight: 'normal' },
            count: 5,
            maxTicksLimit: 5,
            autoSkip: false,
            maxRotation: 0,
            minRotation: 0,
            padding: 10,
            stepSize: (yMaxForScale - yMin) / 4,
          },
        },
        x: {
          type: 'time',
          min: minDate ? minDate.getTime() : undefined,
          max: maxDate ? maxDate.getTime() : undefined,
          border: {
            display: true,
            width: 1,
            color: 'rgba(148, 158, 172, 0.5)',
          },
          time: {
            unit: is18To36HourRange ? 'hour' : xAxisUnit,
            // stepSize: 6,
            // unit: xAxisUnit,
            stepSize: xAxisStepSize,
            tooltipFormat: 'dd MMM HH:mm',
            displayFormats: {
              minute: 'h:mm a',
              hour: 'h a',
            },
          },
          ticks: {
            callback: xAxisTickFormatter,
            autoSkip: false,
            maxRotation: 0,
            major: {
              enabled:
                minDate && maxDate
                  ? maxDate.getTime() - minDate.getTime() > 24 * 60 * 60 * 1000
                  : false,
            },
            maxTicksLimit: (() => {
              if (isSingleDayData && xAxisUnit === 'minute' && minDate && maxDate) {
                const durationMinutes = (maxDate.getTime() - minDate.getTime()) / (1000 * 60);
                const expectedTicks = Math.ceil(durationMinutes / (xAxisStepSize || 30)) + 1;
                return Math.min(expectedTicks, 7);
              }
              return 6;
            })(),
            color: '#949EAC',
            font: {
              size: 12,
              family: 'Inter',
              lineHeight: 1.25,
              weight: 'normal',
            },
            padding: 10,
          },
        },
      },
    };
  }, [isSingleDayData, minDate, maxDate, yMaxForScale, actualMaxVal, chartType]);
  return (
    <div className="h-full flex flex-col">
      <Legends
        onLabelToggle={toggleDatasetVisibility}
        visibilityStatus={visibleDatasets}
        chartType={chartType}
      />
      <Line
        className="h-[276px]! max-w-full"
        options={options}
        data={chartData}
        plugins={[htmlLegendPlugin]}
      />
    </div>
  );
}
